import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { scanAccreditation } from "../services/mediaService";
import { ScanAccreditationResponse } from "../types/media";
import { verifyErrorKey } from "../lib/media/scanLogic";
import {
  localizedHolderName,
  summaryToMatchInfo,
  verifyCardState,
} from "../lib/media/scanModeLogic";
import { useLocaleFormat } from "../hooks/useLocaleFormat";
import { useMediaCockpitLabels } from "./media/useMediaCockpitLabels";
import { ALGIERS_TZ } from "../utils/algiersTime";
import ShieldCheckIcon from "./icons/ShieldCheckIcon";
import XCircleIcon from "./icons/XCircleIcon";
import UserIcon from "./icons/UserIcon";
import TicketIcon from "./icons/TicketIcon";
import ClockIcon from "./icons/ClockIcon";
import CalendarIcon from "./icons/CalendarIcon";

/**
 * Keeps the verification page out of search engines whatever the host page
 * template says (vercel.json also sends X-Robots-Tag for /media-verify).
 */
function useNoIndex() {
  useEffect(() => {
    let meta = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    const created = !meta;
    const previous = meta?.getAttribute("content") ?? null;
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "robots");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", "noindex, nofollow");
    return () => {
      if (created) meta?.remove();
      else if (previous !== null) meta?.setAttribute("content", previous);
    };
  }, []);
}

/**
 * MediaVerifyPage - Public verification page for QR codes
 * 
 * Stadium staff scan QR codes with their phone camera.
 * The QR contains a URL like: https://moubarati.com/media-verify?token=uuid
 * This page extracts the token and calls the scan-accreditation edge function.
 */
const MediaVerifyPage: React.FC = () => {
  const { t } = useTranslation("media");
  const { formatDate: formatLocaleDate } = useLocaleFormat();
  const labels = useMediaCockpitLabels();
  useNoIndex();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<ScanAccreditationResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const verifyToken = async () => {
      // Extract token from URL
      const searchParams = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(
        window.location.hash.includes("?")
          ? window.location.hash.split("?")[1]
          : ""
      );
      
      const token = searchParams.get("token") || hashParams.get("token");

      if (!token) {
        setError(t("verify_page.missing_token"));
        setLoading(false);
        return;
      }

      try {
        // gate_location is only honoured for gate staff / the match delegate
        // (signed in): their check from this page is a webapp gate entry. For
        // anyone else the server records a public_link verification.
        const response = await scanAccreditation({ token, gate_location: "webapp_gate" });
        setResult(response);
      } catch (e: any) {
        // The service throws the function's error code (RATE_LIMITED,
        // SYSTEM_ERROR…) or "HTTP <status>": never show either raw.
        setError(t(verifyErrorKey(e?.message)));
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, []);

  // Validity is an Algiers wall-clock instant, whatever the phone's timezone.
  const formatDate = (dateString: string) =>
    formatLocaleDate(dateString, {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: ALGIERS_TZ,
    });

  // Known decision codes get their own sentence; anything else keeps the
  // generic "not valid" line rather than a raw code.
  const invalidReason = (code?: string) => {
    const key = verifyErrorKey(code);
    return key === "scan.verify_errors.generic"
      ? t("verify_page.accreditation_invalid_desc")
      : t(key);
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-3 border-b-3 border-primary-500 mx-auto mb-4"></div>
          <p className="text-xl text-white/80">{t("verify_page.loading")}</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="bg-rose-900/50 border-3 border-rose-500 rounded-2xl p-6 max-w-md mx-auto">
          <div className="text-center">
            <XCircleIcon className="h-20 w-20 text-rose-400 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-rose-200 mb-2">
              {t("verify_page.verify_failed")}
            </h2>
            <p className="text-lg text-rose-200/80">{error}</p>
          </div>
        </div>
      );
    }

    const cardState = result ? verifyCardState(result) : null;

    if (result && (cardState === "before_match" || cardState === "not_yet_valid")) {
      return (
        <ContextCard tone="info" icon={<CalendarIcon className="h-16 w-16 text-sky-300 mx-auto mb-4" />}>
          <h2 className="text-2xl font-bold text-sky-100 mb-2">
            {cardState === "before_match"
              ? t("verify_page.before_match_title")
              : t("verify_page.not_yet_title")}
          </h2>
          <p className="text-base text-sky-100/85">{contextDescription(result, cardState)}</p>
          <HolderLine result={result} tone="info" />
          <p className="mt-4 rounded-lg bg-white/5 p-3 text-xs text-sky-100/70">
            {t("verify_page.self_check_hint")}
          </p>
        </ContextCard>
      );
    }

    if (result && cardState === "match_over") {
      return (
        <ContextCard tone="muted" icon={<ClockIcon className="h-16 w-16 text-slate-300 mx-auto mb-4" />}>
          <h2 className="text-2xl font-bold text-slate-100 mb-2">
            {t("verify_page.match_over_title")}
          </h2>
          <p className="text-base text-slate-200/80">{contextDescription(result, cardState)}</p>
          <HolderLine result={result} tone="muted" />
        </ContextCard>
      );
    }

    if (result && !result.valid) {
      return (
        <div className="bg-rose-900/50 border-3 border-rose-500 rounded-2xl p-6 max-w-md mx-auto">
          <div className="text-center">
            <XCircleIcon className="h-20 w-20 text-rose-400 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-rose-200 mb-2">
              {t("verify_page.accreditation_invalid")}
            </h2>
            <p className="text-lg text-rose-200/80">
              {invalidReason(result.error)}
            </p>
          </div>
        </div>
      );
    }

    if (result && result.valid && result.accreditation) {
      const acc = result.accreditation;
      const holderPhoto = acc.photo_url ?? acc.holder_photo ?? null;
      const holderName = localizedHolderName(acc, labels.displayMode) ?? acc.holder_name;
      const isEntry = isEntryScan(result);
      const matchInfo = result.match ? summaryToMatchInfo(result.match) : null;
      return (
        <div className="bg-emerald-900/50 border-3 border-emerald-500 rounded-2xl p-6 max-w-md mx-auto">
          {/* Valid header */}
          <div className="text-center mb-6">
            <ShieldCheckIcon className="h-20 w-20 text-emerald-400 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-emerald-200">
              {t("verify_page.accreditation_valid")}
            </h2>
          </div>

          {/* Photo and Name */}
          <div className="flex items-center gap-4 mb-6 p-4 bg-emerald-900/30 rounded-xl">
            {holderPhoto ? (
              <img
                src={holderPhoto}
                alt={holderName}
                className="h-20 w-20 rounded-full object-cover border-3 border-emerald-400"
              />
            ) : (
              <div className="h-20 w-20 rounded-full bg-emerald-800/50 flex items-center justify-center border-3 border-emerald-400">
                <UserIcon className="h-10 w-10 text-emerald-300" />
              </div>
            )}
            <div>
              <h3 className="text-2xl font-bold text-white">{holderName}</h3>
              <p className="text-lg text-emerald-200">{acc.organization}</p>
            </div>
          </div>

          {/* Details Grid — every field here belongs to the authorized-scanner
              tier of the scan payload. This page is public and normally calls
              scan-accreditation anonymously, so it usually receives only
              holder_name + organization: each block must be guarded. */}
          <div className="space-y-3">
            {acc.media_type && (
              <DetailRow
                label={t("verify_page.label_type")}
                value={t(`media_type.${acc.media_type}`, { defaultValue: acc.media_type })}
              />
            )}
            {acc.valid_until && (
              <DetailRow
                label={t("verify_page.label_valid_until")}
                value={formatDate(acc.valid_until)}
              />
            )}
            {matchInfo && (
              <DetailRow label={t("verify_page.label_match")} value={matchLabel(matchInfo)} />
            )}
            {acc.qr_kind && (
              <DetailRow
                label={t("verify_page.label_qr_kind")}
                value={t(`scan.qr_kind.${acc.qr_kind}`)}
              />
            )}

            {/* Approved Zones */}
            {acc.approved_zones && acc.approved_zones.length > 0 && (
              <div className="p-3 bg-emerald-900/30 rounded-lg">
                <div className="text-sm text-emerald-200/60 mb-2">{t("verify_page.approved_zones")}</div>
                <div className="flex flex-wrap gap-2">
                  {acc.approved_zones.map((zone) => (
                    <span
                      key={zone}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-emerald-500/30 text-emerald-200 border border-emerald-400/50"
                    >
                      {t(`zone_type.${zone}`, { defaultValue: zone })}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <p
            className={
              isEntry
                ? "mt-4 rounded-lg bg-emerald-500/20 p-3 text-center text-sm font-medium text-emerald-100"
                : "mt-4 rounded-lg bg-white/5 p-3 text-center text-xs text-emerald-100/70"
            }
          >
            {isEntry ? t("verify_page.entry_recorded") : t("verify_page.verification_only")}
          </p>
        </div>
      );
    }

    return null;
  };

  // v2 says it outright; v1 gives the full tier (with an id) only to gate staff
  // / the match delegate, and only their scans are recorded as an entry.
  const isEntryScan = (r: ScanAccreditationResponse) =>
    r.recorded_as ? r.recorded_as === "entry" : Boolean(r.accreditation?.id);

  const matchLabel = (m: ReturnType<typeof summaryToMatchInfo>) =>
    [
      labels.teams(m),
      m.match_date ? labels.matchDay(m, { year: "numeric" }) : null,
      labels.kickoffTime(m),
    ]
      .filter(Boolean)
      .join(" • ");

  const contextDescription = (
    r: ScanAccreditationResponse,
    state: "before_match" | "not_yet_valid" | "match_over",
  ) => {
    const match = r.match ? matchLabel(summaryToMatchInfo(r.match)) : null;
    if (state === "before_match") {
      return match
        ? t("verify_page.before_match_desc", { match })
        : t("verify_page.before_match_desc_no_match");
    }
    if (state === "not_yet_valid") {
      const from = r.valid_from ? formatDate(r.valid_from) : null;
      const opens = from
        ? t("verify_page.not_yet_desc", { date: from })
        : t("verify_page.not_yet_desc_no_date");
      return match ? `${t("verify_page.badge_for", { match })} ${opens}` : opens;
    }
    return match
      ? t("verify_page.match_over_desc", { match })
      : t("verify_page.match_over_desc_no_match");
  };

  const HolderLine: React.FC<{ result: ScanAccreditationResponse; tone: "info" | "muted" }> = ({
    result: r,
    tone,
  }) => {
    if (!r.accreditation) return null;
    const name = localizedHolderName(r.accreditation, labels.displayMode);
    return (
      <div
        className={
          tone === "info"
            ? "mt-4 rounded-xl bg-sky-900/30 p-3"
            : "mt-4 rounded-xl bg-slate-800/40 p-3"
        }
      >
        <p className="text-lg font-semibold text-white">{name}</p>
        <p className="text-sm text-white/70">{r.accreditation.organization}</p>
      </div>
    );
  };

  const ContextCard: React.FC<{
    tone: "info" | "muted";
    icon: React.ReactNode;
    children: React.ReactNode;
  }> = ({ tone, icon, children }) => (
    <div
      className={
        tone === "info"
          ? "bg-sky-900/40 border-3 border-sky-400/70 rounded-2xl p-6 max-w-md mx-auto text-center"
          : "bg-slate-800/60 border-3 border-slate-500/70 rounded-2xl p-6 max-w-md mx-auto text-center"
      }
    >
      {icon}
      {children}
    </div>
  );

  const DetailRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <div className="flex justify-between items-center p-3 bg-emerald-900/30 rounded-lg">
      <span className="text-sm text-emerald-200/60">{label}</span>
      <span className="text-lg font-medium text-white">{value}</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-900 bg-[url('https://images.unsplash.com/photo-1522778119026-d647f0565c6a?q=80&w=2940&auto=format&fit=crop')] bg-cover bg-center bg-no-repeat bg-fixed flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm" />
      
      <div className="relative w-full max-w-lg">
        {/* Header - Floating Effect */}
        <div className="flex flex-col items-center space-y-4 mb-8 animate-in fade-in slide-in-from-top-8 duration-700">
          <div className="bg-white/10 p-4 rounded-full border border-white/10 shadow-[0_0_30px_-5px_rgba(255,255,255,0.2)] backdrop-blur-md">
            <TicketIcon className="h-12 w-12 text-primary-400" />
          </div>
          <div className="text-center">
             <h1 className="text-3xl font-bold text-white tracking-tight drop-shadow-md">
              {t("verify_page.page_title")}
            </h1>
            <p className="text-white/60 text-sm mt-1">{t("verify_page.page_subtitle")}</p>
          </div>
        </div>

        {/* Content Card with Glass Effect */}
        <div className="transition-all duration-500 ease-out transform">
           {renderContent()}
        </div>

        {/* Gate agents: the camera of a signed-in agent's phone opens this
            page too, but only the scan mode records entries in a row. A
            recognised agent gets a shortcut; anyone else a discreet invitation
            (the scan mode shows the login first when signed out). */}
        {!loading && (
          <div className="mt-6 text-center">
            <a
              href="/media/scan"
              className="text-xs text-white/50 underline-offset-2 hover:text-white/80 hover:underline"
            >
              {result && isEntryScan(result)
                ? t("verify_page.open_scan_mode")
                : t("verify_page.gate_agent_prompt")}
            </a>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-white/30 font-medium tracking-widest uppercase">
            {t("verify_page.footer")}
          </p>
        </div>
      </div>
    </div>
  );
};

export default MediaVerifyPage;
